import { memo, useState } from 'react';
import { HStack, IconButton, Tooltip } from '@chakra-ui/react';
import ThumbDownSharpIcon from '@gamma/ui/icons/ThumbDownSharpIcon';
import ThumbUpSharpIcon from '@gamma/ui/icons/ThumbUpSharpIcon';
import { FeedbackPopoverForm } from './FeedbackPopoverForm';
import { type FeedbackConfig, FeedbackType } from '@investigator/shared/types';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface FeedbackThumbsProps {
  config: FeedbackConfig;
  trackingContext?: Record<string, unknown>;
  spacing?: number;
  buttonSize?: ButtonSize;
  isDisabled?: boolean;
}

/**
 * Renders thumbs up/down buttons for collecting user feedback.
 *
 * Features:
 * - Allows multiple feedback submissions
 * - Tooltip on hover (disabled when popover is open)
 * - Popover form on click
 * - Localized labels and tooltips
 *
 * Note: This component does not have a dedicated test file because it's a simple
 * composition component that passes props to FeedbackPopoverForm. Its functionality
 * is comprehensively tested in FeedbackPopoverForm.spec.tsx, which covers all
 * user interactions, form submissions, and edge cases.
 */
export const FeedbackThumbs = memo(
  ({
    config,
    trackingContext,
    spacing = 0.5,
    buttonSize = 'sm',
    isDisabled = false,
  }: FeedbackThumbsProps): JSX.Element => {
    const { positive, negative, tracking } = config;
    const [openPopover, setOpenPopover] = useState<
      null | 'positive' | 'negative'
    >(null);

    return (
      <HStack spacing={spacing} data-testid="customer-feedback-thumbs">
        <Tooltip
          label={positive.tooltip}
          placement="bottom"
          isDisabled={openPopover === 'positive'}
          openDelay={500}
          shouldWrapChildren
        >
          <FeedbackPopoverForm
            type={FeedbackType.positive}
            copy={positive}
            tracking={tracking}
            trackingContext={trackingContext}
            isOpen={openPopover === 'positive'}
            onOpenChange={(isOpen) =>
              setOpenPopover(isOpen ? 'positive' : null)
            }
          >
            <IconButton
              aria-label={positive.ariaLabel}
              icon={<ThumbUpSharpIcon size="md" />}
              size={buttonSize}
              variant="ghost"
              colorScheme="gray"
              isDisabled={isDisabled}
              data-testid="feedback-thumbs-up-button"
              _hover={{
                bg: 'whiteAlpha.200',
              }}
            />
          </FeedbackPopoverForm>
        </Tooltip>
        <Tooltip
          label={negative.tooltip}
          placement="bottom"
          isDisabled={openPopover === 'negative'}
          openDelay={500}
          shouldWrapChildren
        >
          <FeedbackPopoverForm
            type={FeedbackType.negative}
            copy={negative}
            tracking={tracking}
            trackingContext={trackingContext}
            isOpen={openPopover === 'negative'}
            onOpenChange={(isOpen) =>
              setOpenPopover(isOpen ? 'negative' : null)
            }
          >
            <IconButton
              aria-label={negative.ariaLabel}
              icon={<ThumbDownSharpIcon size="md" />}
              size={buttonSize}
              variant="ghost"
              colorScheme="gray"
              isDisabled={isDisabled}
              data-testid="feedback-thumbs-down-button"
              _hover={{
                bg: 'whiteAlpha.200',
              }}
            />
          </FeedbackPopoverForm>
        </Tooltip>
      </HStack>
    );
  },
);
